import {
  DownloadOutlined,
  FileTextOutlined,
  FormOutlined,
} from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppDescription from '@nxsys/components/AppDescription';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppRateTable from '@nxsys/components/AppRateTable';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultStatusOptions } from '@nxsys/constants/AppConstants';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { assignmentActions } from '@nxsys/store/slices/assignment/reducer/assignmentSlice';
import { Assignment } from '@nxsys/store/slices/assignment/types';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { countryList, formatStatus, getFileNameFromUrl } from '@nxsys/utils';
import { Col, Descriptions, Row, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import AssignmentForm, {
  AssignmentFormRef,
  AssignmentFormValues,
} from '../components/AssignmentForm';

const { Text } = Typography;

const ADDRESS_TYPE = {
  INVOICING_ADDRESS: 'INVOICING_ADDRESS',
  WORK_LOCATION: 'WORK_LOCATION',
};

const AssignmentDetail = () => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const formRef = useRef<AssignmentFormRef>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { isAgency } = useSelector((state: AppState) => state.appStore);

  const [loadingAssignmentDetail, setLoadingAssignmentDetail] =
    useState<boolean>(false);
  const [assignmentDetail, setAssignmentDetail] = useState<Assignment | null>(
    null,
  );
  const [isUpdatingAssignment, setIsUpdatingAssignment] =
    useState<boolean>(false);

  const { goBack } = useGoBackByQuery();

  const loading = loadingAssignmentDetail || isUpdatingAssignment;

  const [isEditing, setIsEditing] = useState(false);

  const getDetail = async (id: string) => {
    setLoadingAssignmentDetail(true);
    const res = await dispatch(
      assignmentActions.getAssignmentById({ id }),
    ).unwrap();
    setAssignmentDetail(res.data);
    setLoadingAssignmentDetail(false);
  };

  useEffect(() => {
    if (params.assignmentId) getDetail(params.assignmentId);
  }, [params.assignmentId]);

  const handleGoBack = () =>
    goBack({ fromPathQueryKey: 'from', fallbackPathDefault: '/assignments' });
  const toggleEditing = () => setIsEditing((v) => !v);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <div className="flex items-center gap-3">
          <AppPageTitle
            prefix={messages['assignment.title']}
            separator=""
            title={assignmentDetail?.assignmentName ?? ''}
          />
          <AppTagStatus
            className={`tag-popup ${formatStatus(assignmentDetail?.status ?? '')}`}
          >
            {assignmentDetail?.status}
          </AppTagStatus>
        </div>
      </div>
    );
  }, [messages, assignmentDetail?.status]);

  const renderRightContent = useCallback(() => {
    if (!isEditing) {
      if (isAgency) {
        return (
          <div className="flex items-center gap-2">
            {assignmentDetail?.file && (
              <AppButton
                variant="outlined"
                type="primary"
                name={messages['common.download']}
                leftIcon={<DownloadOutlined />}
                onClick={() => window.open(assignmentDetail.file, '_blank')}
              />
            )}
          </div>
        );
      }
      return (
        <div className="flex items-center gap-2">
          {assignmentDetail?.file && (
            <AppButton
              variant="outlined"
              type="primary"
              name={messages['common.download']}
              leftIcon={<DownloadOutlined />}
              onClick={() => window.open(assignmentDetail.file, '_blank')}
            />
          )}
          <AppButton
            type="primary"
            name={messages['common.edit']}
            leftIcon={<FormOutlined />}
            onClick={toggleEditing}
            disabled={loading}
          />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.cancel']}
          onClick={toggleEditing}
        />
        <AppButton
          type="primary"
          name={messages['common.save']}
          onClick={() => formRef.current?.submit()}
          loading={isUpdatingAssignment}
          disabled={isUpdatingAssignment}
        />
      </div>
    );
  }, [
    messages,
    isEditing,
    assignmentDetail?.file,
    loading,
    isUpdatingAssignment,
    formRef,
  ]);

  const assignmentDetailItems = [
    {
      label: messages['assignment.detail.assignmentName'],
      value: assignmentDetail?.assignmentName ?? '',
    },
    {
      label: messages['common.status'],
      value:
        defaultStatusOptions.find(
          (status) => status.value === assignmentDetail?.status,
        )?.label ?? '',
    },
    {
      label: messages['assignment.detail.agencyName'],
      value: assignmentDetail?.agencyRef?.name ?? '',
    },
    {
      label: messages['assignment.detail.projectRef'],
      value: assignmentDetail?.projectRef ?? '',
    },
    {
      label: messages['assignment.detail.employeeSubcontractor'],
      value: assignmentDetail?.subcontractorRef?.fullName ?? '',
    },
    {
      label: messages['assignment.detail.startDate'],
      value: assignmentDetail?.startDate
        ? moment(assignmentDetail?.startDate).format('DD/MM/YYYY')
        : '',
    },
    {
      label: messages['assignment.detail.clientRef'],
      value: assignmentDetail?.clientRef ?? '',
    },
    {
      label: messages['assignment.detail.endDate'],
      value: assignmentDetail?.endDate
        ? moment(assignmentDetail?.startDate).format('DD/MM/YYYY')
        : '',
    },
    {
      label: messages['assignment.detail.siteId'],
      value: assignmentDetail?.siteRef?.id ?? '',
    },
    {
      label: messages['common.attachment'],
      value: assignmentDetail?.file ? (
        <div
          className="truncate"
          onClick={() => window.open(assignmentDetail.file, '_blank')}
        >
          <FileTextOutlined className="mr-1" />
          {getFileNameFromUrl(assignmentDetail?.file)}
        </div>
      ) : (
        ''
      ),
      className: 'max-w-full cursor-pointer text-primary-500',
    },
  ];

  const renderAddressCard = (
    addressType: (typeof ADDRESS_TYPE)[keyof typeof ADDRESS_TYPE],
  ) => {
    const title =
      addressType === ADDRESS_TYPE.INVOICING_ADDRESS
        ? messages['assignment.detail.invoicingAddress']
        : messages['assignment.detail.workLocation'];

    return (
      <AppCard title={title} style={{ marginTop: 16 }}>
        <div style={{ padding: 20 }}>
          <AppDescription
            column={1}
            labelStyle={{ width: 160 }}
            bordered={false}
            colon={false}
            size="middle"
          >
            <Descriptions.Item label={messages['assignment.address.address1']}>
              {assignmentDetail?.invoicingAddress?.address?.[0] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.address2']}>
              {assignmentDetail?.invoicingAddress?.address?.[1] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.address3']}>
              {assignmentDetail?.invoicingAddress?.address?.[2] ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['common.cityTown']}>
              {assignmentDetail?.invoicingAddress?.city ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.county']}>
              {assignmentDetail?.invoicingAddress?.county ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.postCode']}>
              {assignmentDetail?.invoicingAddress?.postCode ?? ''}
            </Descriptions.Item>
            <Descriptions.Item label={messages['assignment.address.country']}>
              {assignmentDetail?.invoicingAddress?.country ?? ''}
            </Descriptions.Item>
          </AppDescription>
        </div>
      </AppCard>
    );
  };

  const handleSave = async (values: AssignmentFormValues) => {
    if (!assignmentDetail?.id) return;

    try {
      setIsUpdatingAssignment(true);
      const params: any = {
        agencyRef: values.agencyName?.value,
        assignmentName: values.assignmentName ?? '',
        clientRef: values.clientRef ?? '',
        endDate: moment(values.endDate).toISOString(),
        invoicingAddress: {
          address: [
            values.invoicingAddress?.address1 ?? '',
            values.invoicingAddress?.address2 ?? '',
            values.invoicingAddress?.address3 ?? '',
          ],
          city: values.invoicingAddress?.city ?? '',
          county: values.invoicingAddress?.county ?? '',
          postCode: values.invoicingAddress?.postCode ?? '',
          country: (values.invoicingAddress?.country as any)?.value ?? '',
        },
        projectRef: values.projectRef ?? '',
        rates: values.rates?.map((rate) => {
          // fullData holds the selected PaymentRate entity (has id, type, code)
          const fullData = (rate as any).rateCode?.fullData;
          return {
            rateId: fullData?.id ?? '',
            rateCode: (rate.rateCode as any).value,
            rateType: fullData?.type || rate.rateType || '',
            description: rate.description,
            rate: rate.rate,
            vatRate: rate.vatRate,
            vatDetails: (rate.vatDetails as any).label,
            includeInCisCalculation: fullData?.includeInCisCalculation ?? false,
          };
        }),
        siteId: values.siteId?.value || null,
        startDate: moment(values.startDate).toISOString(),
        status: (values.status as any).value ?? '',
        subcontractorRef: values.employeeAndSubcontractor?.value ?? '',
        workLocation: {
          address: [
            values.workLocation?.address1 ?? '',
            values.workLocation?.address2 ?? '',
            values.workLocation?.address3 ?? '',
          ],
          city: values.workLocation?.city ?? '',
          county: values.workLocation?.county ?? '',
          postCode: values.workLocation?.postCode ?? '',
          country: values.workLocation?.country?.value ?? '',
        },
        file: null,
      };
      const isUploadFile = !!(values.attachment as File)?.name;
      if (isUploadFile) {
        const res = await dispatch(
          commonActions.uploadFileToS3({ file: values.attachment as File }),
        ).unwrap();
        params.file = res.url;
      }
      await dispatch(
        assignmentActions.updateAssignmentById({
          id: assignmentDetail?.id,
          payload: params,
        }),
      ).unwrap();
      await getDetail(assignmentDetail.id);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdatingAssignment(false);
    }
  };

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loading}
    >
      {!isEditing ? (
        <div style={{ padding: 4 }}>
          <AppCard title={messages['assignment.section.detail']}>
            <div style={{ padding: 20 }}>
              <AppDescription
                column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                labelStyle={{ width: 200 }}
                bordered={false}
                colon={false}
                size="middle"
              >
                {assignmentDetailItems.map((it, idx) => (
                  <Descriptions.Item key={idx} label={it.label}>
                    <Text className={it.className}>{it.value}</Text>
                  </Descriptions.Item>
                ))}
              </AppDescription>
            </div>
          </AppCard>

          <Row gutter={[16, 16]}>
            <Col sm={24} md={12}>
              {renderAddressCard(ADDRESS_TYPE.INVOICING_ADDRESS)}
            </Col>
            <Col sm={24} md={12}>
              {renderAddressCard(ADDRESS_TYPE.WORK_LOCATION)}
            </Col>
          </Row>

          <AppRateTable
            data={(assignmentDetail?.rates as any) ?? []}
            listHidden={['units', 'net', 'total']}
          />
        </div>
      ) : (
        <AssignmentForm
          defaultValues={{
            assignmentName: assignmentDetail?.assignmentName ?? '',
            projectRef: assignmentDetail?.projectRef ?? '',
            clientRef: assignmentDetail?.clientRef ?? '',
            status: {
              label: assignmentDetail?.status,
              value: assignmentDetail?.status,
            } as any,
            siteId: assignmentDetail?.siteRef?.id
              ? {
                  label: assignmentDetail?.siteRef?.id,
                  value: assignmentDetail?.siteRef?.id,
                }
              : null,
            employeeAndSubcontractor: {
              label: assignmentDetail?.subcontractorRef?.fullName,
              value: assignmentDetail?.subcontractorRef?.id,
            },
            agencyName: {
              label: assignmentDetail?.agencyRef?.name,
              value: assignmentDetail?.agencyRef?.id,
            },
            startDate: assignmentDetail?.startDate,
            endDate: assignmentDetail?.endDate,
            invoicingAddress: {
              address1: assignmentDetail?.invoicingAddress?.address[0],
              address2: assignmentDetail?.invoicingAddress?.address[1],
              address3: assignmentDetail?.invoicingAddress?.address[2],
              city: assignmentDetail?.invoicingAddress?.city,
              county: assignmentDetail?.invoicingAddress?.county,
              postCode: assignmentDetail?.invoicingAddress?.postCode,
              country:
                countryList.find(
                  (c) =>
                    c.value === assignmentDetail?.invoicingAddress?.country,
                ) || null,
            },
            workLocation: {
              address1: assignmentDetail?.workLocation?.address[0],
              address2: assignmentDetail?.workLocation?.address[1],
              address3: assignmentDetail?.workLocation?.address[2],
              city: assignmentDetail?.workLocation?.city,
              county: assignmentDetail?.workLocation?.county,
              postCode: assignmentDetail?.workLocation?.postCode,
              country:
                countryList.find(
                  (c) => c.value === assignmentDetail?.workLocation?.country,
                ) || null,
            },
            rates: assignmentDetail?.rates?.map((rate) => ({
              ...(rate as any),
              rateCode: {
                label: rate.rateCode,
                value: rate.rateCode,
                fullData: rate,
              },
              description: rate.description,
              rate: rate.rate,
              rateType: rate.rateType,
              vatDetails: { label: rate.vatDetails, value: rate.vatDetails },
              vatRate: rate.vatRate,
            })),
            attachment: assignmentDetail?.file,
          }}
          formRef={formRef}
          onSubmit={handleSave}
        />
      )}
    </AppsContainer>
  );
};

export default AssignmentDetail;
